import {
  Department,
  DepartmentFieldError,
  IDepartment,
  IDepartmentFieldError
} from '../department';
import { State } from '../state/state';
import { IUserData, User } from '../user';

import {
  IFormField,
  IFormFieldData,
  IFormFieldOptionData,
  IFormTemplate,
  IFormTemplateData,
  FormFieldTypeEnum,
  IFormSectionData,
  IFormSection,
  IFormSectionField,
  IFormSectionFieldData,
  IFormFieldOption,
  IFormTemplateFieldError,
  IFormSectionFieldErrorData,
  IFormTemplateFieldErrorData,
  IFormSectionFieldError,
  IFormSectionFieldFieldErrorData,
  IFormSectionFieldFieldError,
  IFormFieldFieldErrorData,
  IFormFieldFieldError,
  IFormFieldOptionTodo,
  IFormFieldOptionTodoData,
  IFormFieldOptionTodoTemplateData,
  IFormFieldOptionTodoTemplate,
  ConstraintActionEnum,
  IFormSectionFieldConstraint,
  IFormSectionFieldConstraintConditionData,
  IFormSectionFieldConstraintCondition,
  IFormTemplateStatusData,
  IFormTemplateStatus,
  FormTemplateStateEnum,
  IFormSectionFieldConstraintData,
  IFormFieldType,
  IFormFieldOptionFieldError,
  IFormFieldStyleFieldError
} from './form-template.types';

export class FormTemplate implements IFormTemplate {
  readonly id?: number;
  readonly title: string;
  readonly description: string;
  readonly state: IFormTemplateStatus = new FormTemplateStatus();
  readonly owner?: string;
  readonly sections?: IFormSection[];
  readonly created_at?: Date;
  readonly updated_at?: Date;

  constructor(data: IFormTemplateData) {
    if (data.id) {
      this.id = data.id;
    }

    this.title = data.title ?? '';
    this.description = data.description ?? '';

    if (typeof data.state == 'number') {
      this.state = new FormTemplateStatus({ value: data.state });
    } else {
      this.state = data.state;
    }

    if (data.sections) {
      this.sections = data.sections.map(data => new FormSection(data));
    }

    this.owner = data.owner ?? '';

    if (data.created_at) {
      this.created_at = new Date(data.created_at);
    }

    if (data.updated_at) {
      this.updated_at = new Date(data.updated_at);
    }
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }
}

class FormTemplateState extends State<FormTemplateStateEnum> {
  static readonly Draft = new State({
    value: FormTemplateStateEnum.Draft,
    display_name: FormTemplateStateEnum[FormTemplateStateEnum.Draft],
    message: 'Draft',
    icon: 'mdi-file-document-outline',
    color: 'draft'
  });

  public static readonly Published = new State({
    value: FormTemplateStateEnum.Published,
    display_name: FormTemplateStateEnum[FormTemplateStateEnum.Published],
    message: 'Published',
    icon: 'mdi-file-send-outline',
    color: 'success'
  });

  public static readonly Default = new State({
    value: -1,
    display_name: '',
    color: 'white'
  });
}
export class FormTemplateStatus implements IFormTemplateStatusData {
  readonly value: number = FormTemplateStateEnum.Draft;
  readonly display_name?: string = 'Draft';

  constructor(data?: IFormTemplateStatusData) {
    if (data) {
      this.value = data.value;
      if (data.display_name) {
        this.display_name = data.display_name;
      }
    }
  }

  getCurrentState(): State<FormTemplateStateEnum> {
    let state = null;
    switch (this.value) {
      case FormTemplateStateEnum.Draft:
        state = FormTemplateState.Draft;
        break;

      case FormTemplateStateEnum.Published:
        state = FormTemplateState.Published;
        break;

      default:
        state = FormTemplateState.Default;
    }
    return state;
  }
}

export class FormSection implements IFormSection {
  readonly id?: number;
  readonly title: string;
  readonly order: number;
  readonly section_fields: IFormSectionField[];

  constructor(data: IFormSectionData) {
    if (data.id) {
      this.id = data.id;
    }

    this.title = data.title ?? '';
    this.order = data.order;

    this.section_fields = data.section_fields.map(
      data => new FormSectionField(data)
    );
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }
}

export class FormSectionField implements IFormSectionField {
  readonly id?: number;
  readonly order: number;
  readonly width: number;
  readonly field_template: IFormField | number;
  readonly required: boolean;
  readonly disabled: boolean;
  readonly create_todo: boolean;
  readonly constraints: IFormSectionFieldConstraint[] = [];

  constructor(data: IFormSectionFieldData) {
    if (data.id) {
      this.id = data.id;
    }
    this.order = data.order;
    this.width = data.width;
    this.required = data.required ?? false;
    this.disabled = data.disabled ?? false;

    if (typeof data.field_template == 'number') {
      this.field_template = data.field_template;
    } else {
      this.field_template = new FormField(data.field_template);
    }
    this.create_todo = data.create_todo ?? false;
    this.constraints = data.constraints.map(
      constraint => new FormSectionFieldConstraint(constraint)
    );
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }
}

export class FormSectionFieldConstraint implements IFormSectionFieldConstraint {
  readonly id?: number;
  readonly action: ConstraintActionEnum;
  readonly conditions: IFormSectionFieldConstraintCondition[] = [];

  constructor(data: IFormSectionFieldConstraintData) {
    if (data.id) {
      this.id = data.id;
    }

    this.action = data.action;

    this.conditions = data.conditions.map(
      condition => new FormSectionFieldConstraintCondition(condition)
    );
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }
}

export class FormSectionFieldConstraintCondition
  implements IFormSectionFieldConstraintCondition {
  readonly id?: number;
  readonly field_option: number;
  readonly form_section_field: number;

  constructor(data: IFormSectionFieldConstraintConditionData) {
    if (data.id) {
      this.id = data.id;
    }
    this.field_option = data.field_option;
    this.form_section_field = data.form_section_field;
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }
}

export class FormFieldType implements IFormFieldType {
  readonly value: FormFieldTypeEnum;
  readonly text: string;
  readonly icon: string;

  constructor(data: IFormFieldType) {
    this.value = data.value;
    this.text = data.text;
    this.icon = data.icon;
  }

  public toString(): string {
    return this.text;
  }

  static Label = new FormFieldType({
    icon: 'mdi-format-text',
    text: 'Label',
    value: FormFieldTypeEnum.Label
  });

  static Textfield = new FormFieldType({
    icon: 'mdi-form-textbox',
    text: 'Short input box',
    value: FormFieldTypeEnum.Textfield
  });

  static Textarea = new FormFieldType({
    icon: 'mdi-text-recognition',
    text: 'Long input box',
    value: FormFieldTypeEnum.Textarea
  });

  static Combobox = new FormFieldType({
    icon: 'mdi-form-dropdown',
    text: 'Dropdown',
    value: FormFieldTypeEnum.Combobox
  });

  static DatePicker = new FormFieldType({
    icon: 'mdi-calendar-range',
    text: 'Date',
    value: FormFieldTypeEnum.DatePicker
  });

  static Checkbox = new FormFieldType({
    icon: 'mdi-order-bool-ascending-variant',
    text: 'Checklist',
    value: FormFieldTypeEnum.Checkbox
  });

  static Radiobutton = new FormFieldType({
    icon: 'mdi-radiobox-marked',
    text: 'Radio Button',
    value: FormFieldTypeEnum.Radiobutton
  });

  static FileField = new FormFieldType({
    icon: 'mdi-paperclip',
    text: 'File',
    value: FormFieldTypeEnum.FileField
  });

  static getFormFieldTypeList(): IFormFieldType[] {
    return [
      FormFieldType.Label,
      FormFieldType.Textfield,
      FormFieldType.Textarea,
      FormFieldType.Combobox,
      FormFieldType.DatePicker,
      FormFieldType.Checkbox,
      FormFieldType.Radiobutton,
      FormFieldType.FileField
    ];
  }
}

export class FormField implements IFormField {
  readonly id?: number;
  readonly label: string;
  readonly meta_label: string;
  readonly hint: string;
  readonly type: FormFieldTypeEnum;
  readonly options: FormFieldOption[] = [];

  constructor(data: IFormFieldData) {
    if (data.id) {
      this.id = data.id;
    }
    this.hint = data.hint ?? '';
    this.label = data.label ?? '';
    this.meta_label = data.meta_label ?? '';
    this.type = data.type;

    if (data.options) {
      this.options = data.options.map(data => new FormFieldOption(data));
    }
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }
}

export class FormFieldOption implements IFormFieldOption {
  readonly id?: number;
  readonly value: string;
  readonly order: number;
  readonly field: number;

  constructor(data: IFormFieldOptionData) {
    if (data.id) {
      this.id = data.id;
    }

    this.value = data.value;
    this.field = data.field;
    this.order = data.order;
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }
}

export class FormFieldOptionTodo implements IFormFieldOptionTodo {
  readonly id?: number;
  readonly field_option: number;
  readonly todo_template: IFormFieldOptionTodoTemplate | number;
  readonly section_field: number;

  constructor(data: IFormFieldOptionTodoData) {
    if (data.id) {
      this.id = data.id;
    }

    this.field_option = data.field_option;

    if (typeof data.todo_template == 'number') {
      this.todo_template = data.todo_template;
    } else {
      this.todo_template = new FormFieldOptionTodoTemplate(data.todo_template);
    }

    this.section_field = data.section_field;
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }
}

export class FormFieldOptionTodoTemplate
  implements IFormFieldOptionTodoTemplate {
  readonly id?: number;
  readonly todo_text: string;
  readonly todo_department: IDepartment | number;
  readonly assignee: IUserData | number;
  readonly order: number;

  constructor(data: IFormFieldOptionTodoTemplateData) {
    if (data.id) {
      this.id = data.id;
    }

    this.todo_text = data.todo_text ?? '';

    if (typeof data.todo_department == 'number') {
      this.todo_department = data.todo_department;
    } else {
      this.todo_department = new Department(data.todo_department);
    }

    if (typeof data.assignee == 'number') {
      this.assignee = data.assignee;
    } else {
      this.assignee = new User(data.assignee);
    }

    this.order = data.order;
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }
}

export class FormTemplateFieldError implements IFormTemplateFieldError {
  readonly title: string[] = [];
  readonly description: string[] = [];
  readonly sections: IFormSectionFieldError[] = [];
  readonly owner: string[] = [];

  constructor(data?: IFormTemplateFieldErrorData) {
    if (data) {
      if (data.title) {
        this.title = data.title;
      }
      if (data.description) {
        this.description = data.description;
      }
      if (data.owner) {
        this.owner = data.owner;
      }
      if (data.sections) {
        this.sections = data.sections.map(
          section => new FormSectionFieldError(section)
        );
      }
    }
  }

  clearErrors(): void {
    this.title.splice(0, this.title.length);
    this.description.splice(0, this.description.length);
    this.sections.splice(0, this.sections.length);
  }

  static isFormTemplateFieldErrorData(
    data: IFormTemplateFieldErrorData
  ): boolean {
    if (data.title && Array.isArray(data.title)) {
      return true;
    } else if (data.description && Array.isArray(data.description)) {
      return true;
    } else if (data.sections && Array.isArray(data.sections)) {
      return true;
    }
    return false;
  }
}

export class FormSectionFieldError implements IFormSectionFieldError {
  readonly order: string[] = [];
  readonly title: string[] = [];
  readonly section_fields: IFormSectionFieldFieldError[] = [];

  constructor(data?: IFormSectionFieldErrorData) {
    if (data) {
      if (data.order) {
        this.order = data.order;
      }
      if (data.section_fields) {
        this.section_fields = data.section_fields.map(
          section => new FormSectionFieldFieldError(section)
        );
      }
    }
  }

  clearErrors(): void {
    this.title.splice(0, this.title.length);
    this.order.splice(0, this.order.length);
    this.section_fields.splice(0, this.section_fields.length);
  }

  static isEmployeeFormFieldSubmissionDataFieldError(
    data: IFormSectionFieldErrorData
  ): boolean {
    if (data.title && Array.isArray(data.title)) {
      return true;
    } else if (data.order && Array.isArray(data.order)) {
      return true;
    } else if (data.section_fields && Array.isArray(data.section_fields)) {
      return true;
    }
    return false;
  }
}

export class FormSectionFieldFieldError implements IFormSectionFieldFieldError {
  readonly field: IFormFieldFieldError[] = [];
  readonly width: string[] = [];
  readonly order: string[] = [];

  constructor(data?: IFormSectionFieldFieldErrorData) {
    if (data) {
      if (data.order) {
        this.order = data.order;
      }
      if (data.width) {
        this.width = data.width;
      }
      if (data.field) {
        this.field = data.field.map(
          field => new FormFieldFieldErrorData(field)
        );
      }
    }
  }

  clearErrors(): void {
    this.width.splice(0, this.width.length);
    this.order.splice(0, this.order.length);
    this.field.splice(0, this.field.length);
  }

  static isEmployeeFormFieldSubmissionDataFieldError(
    data: IFormSectionFieldFieldErrorData
  ): boolean {
    if (data.width && Array.isArray(data.width)) {
      return true;
    } else if (data.order && Array.isArray(data.order)) {
      return true;
    } else if (data.field && Array.isArray(data.field)) {
      return true;
    }
    return false;
  }
}

export class FormFieldFieldErrorData implements IFormFieldFieldError {
  readonly label: string[] = [];
  readonly meta_label: string[] = [];
  readonly hint: string[] = [];
  readonly type: string[] = [];
  readonly required: string[] = [];
  readonly create_todo: string[] = [];
  readonly todo_department: IDepartmentFieldError[] = [];
  readonly options: IFormFieldOptionFieldError[] = [];
  readonly style: IFormFieldStyleFieldError[] = [];
  readonly order: string[] = [];

  constructor(data?: IFormFieldFieldErrorData) {
    if (data) {
      if (data.label) {
        this.label = data.label;
      }
      if (data.meta_label) {
        this.meta_label = data.meta_label;
      }
      if (data.hint) {
        this.hint = data.hint;
      }
      if (data.type) {
        this.type = data.type;
      }
      if (data.required) {
        this.required = data.required;
      }
      if (data.create_todo) {
        this.create_todo = data.create_todo;
      }
      if (data.todo_department) {
        this.todo_department = data.todo_department.map(
          department => new DepartmentFieldError(department)
        );
      }
    }
  }

  clearErrors(): void {
    this.label.splice(0, this.label.length);
    this.order.splice(0, this.order.length);
    this.meta_label.splice(0, this.meta_label.length);
    this.hint.splice(0, this.hint.length);
    this.type.splice(0, this.type.length);
    this.required.splice(0, this.required.length);
    this.create_todo.splice(0, this.create_todo.length);
    this.todo_department.splice(0, this.todo_department.length);
    this.options.splice(0, this.options.length);
    this.style.splice(0, this.style.length);
    this.order.splice(0, this.order.length);
  }

  static isEmployeeFormFieldSubmissionDataFieldError(
    data: IFormFieldFieldErrorData
  ): boolean {
    if (data.label && Array.isArray(data.label)) {
      return true;
    } else if (data.order && Array.isArray(data.order)) {
      return true;
    } else if (data.meta_label && Array.isArray(data.meta_label)) {
      return true;
    } else if (data.hint && Array.isArray(data.hint)) {
      return true;
    } else if (data.required && Array.isArray(data.required)) {
      return true;
    } else if (data.create_todo && Array.isArray(data.create_todo)) {
      return true;
    } else if (data.todo_department && Array.isArray(data.todo_department)) {
      return true;
    } else if (data.options && Array.isArray(data.options)) {
      return true;
    } else if (data.style && Array.isArray(data.style)) {
      return true;
    }
    return false;
  }
}
